/**
 * The reply macros M1–M12 (product doc 13 §5, CSR-142 ask 2) — the copy agents paste into a
 * WhatsApp or SMS reply, kept as rows so support can fix a sentence without a release.
 *
 * The server holds two rules and this screen only reports them: a `{placeholder}` must be one the
 * ticket screen knows how to fill, and the copy must pass the content lint. M9 exists to decline
 * an outcome claim; an edit that turned it into one is what the lint is for.
 *
 * Hindi is written here as its own row. The ticket picker offers it beside the English.
 */
import { useState } from 'react';
import { Input, Select } from '../components/forms';
import { DataTable } from '../components/data';
import { ErrorState, LoadingState } from '../components/feedback';
import { ConfirmAction, ScopeAction, Section } from '../components/kit';
import { useConsole } from '../app/console';
import { errorMessage } from '../lib/api/client';
import {
  type SupportMacroRow,
  useSaveSupportMacro,
  useSupportMacros,
} from '../lib/api/hooks/support';

const LOCALES = ['en', 'hi'];

/** `M5 · Refund initiated (hi)` — one string, because the picker's options are strings. */
export function macroLabel(row: Pick<SupportMacroRow, 'code' | 'title' | 'locale'>): string {
  return `${row.code} · ${row.title}${row.locale === 'en' ? '' : ` (${row.locale})`}`;
}

/**
 * Fill what the ticket already knows and leave the rest standing, braces and all, so the agent
 * sees exactly what is still theirs to write before anything is sent.
 */
export function fillMacro(body: string, values: Readonly<Record<string, string | null>>): string {
  return body.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole);
}

/**
 * The `{tokens}` in `body` the ticket screen cannot fill. The server refuses them too; saying so
 * on the field spares the agent a round trip and names every one, not just the first.
 */
export function unknownPlaceholders(body: string, known: readonly string[]): string[] {
  const used = [...body.matchAll(/\{(\w+)\}/g)].map((m) => m[1]!);
  return [...new Set(used.filter((name) => !known.includes(name)))];
}

export function ReplyMacros() {
  const { scope, toast } = useConsole();
  const macros = useSupportMacros();
  const save = useSaveSupportMacro();
  const [code, setCode] = useState('M1');
  const [locale, setLocale] = useState('en');
  const [title, setTitle] = useState<string | null>(null);
  const [body, setBody] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const rows = macros.data?.items ?? [];
  const codes = [...new Set(rows.map((r) => r.code))];
  const current = rows.find((r) => r.code === code && r.locale === locale);
  // Untouched fields show the stored row; a locale with no row yet starts empty.
  const shownTitle = title ?? current?.title ?? '';
  const shownBody = body ?? current?.body ?? '';
  const unknown = unknownPlaceholders(shownBody, macros.data?.known_placeholders ?? []);
  const pick = (next: () => void) => {
    next();
    setTitle(null);
    setBody(null);
  };

  return (
    <>
      <Section title="Reply macros" note="The copy is product doc 13 §5's; Hindi rows sit beside it">
        {macros.isPending && <LoadingState message="Reading the macros…" />}
        {macros.isError && (
          <ErrorState title="Could not read the macros" cause={errorMessage(macros.error)} />
        )}
        {macros.isSuccess && (
          <DataTable
            density="compact"
            columns={[
              { key: 'code', label: 'Macro' },
              { key: 'locale', label: 'Language' },
              { key: 'title', label: 'Used for' },
              { key: 'fills', label: 'Placeholders' },
            ]}
            rows={rows.map((r) => ({
              id: `${r.code}-${r.locale}`,
              code: r.code,
              locale: r.locale,
              title: r.title,
              fills: r.placeholders.join(', ') || '—',
            }))}
            onRowClick={(r) =>
              pick(() => {
                setCode(String(r.code));
                setLocale(String(r.locale));
              })
            }
          />
        )}
      </Section>

      {macros.isSuccess && (
        <Section
          title="Edit a macro"
          note={`Placeholders it may use: ${macros.data.known_placeholders.join(', ')}`}
        >
          <div style={{ display: 'flex', gap: 'var(--sp-md)', flexWrap: 'wrap' }}>
            <Select label="Macro" value={code} options={codes} onChange={(v) => pick(() => setCode(v))} />
            <Select
              label="Language"
              value={locale}
              options={LOCALES}
              onChange={(v) => pick(() => setLocale(v))}
            />
          </div>
          <Input label="Used for" value={shownTitle} onChange={setTitle} style={{ width: '100%' }} />
          <Input
            label="Copy"
            multiline
            value={shownBody}
            onChange={setBody}
            error={
              unknown.length
                ? `The ticket screen cannot fill ${unknown.map((n) => `{${n}}`).join(', ')}`
                : undefined
            }
            helper={current ? undefined : 'No row in this language yet; saving writes one.'}
            style={{ width: '100%' }}
          />
          <ScopeAction
            scope={scope}
            need="support"
            size="sm"
            disabled={
              save.isPending ||
              shownTitle.trim() === '' ||
              shownBody.trim() === '' ||
              unknown.length > 0
            }
            onClick={() => setConfirming(true)}
          >
            Save
          </ScopeAction>
        </Section>
      )}

      {confirming && (
        <ConfirmAction
          open
          title={`Save ${code} (${locale}) for every agent?`}
          confirmLabel="Save"
          phrase="SAVE"
          consequences={[
            'Every agent who picks this macro from now on starts from this copy.',
            'Replies already sent keep the words they went out with.',
            'The previous copy is kept in the audit log.',
          ]}
          onCancel={() => setConfirming(false)}
          onConfirm={() =>
            save.mutate(
              { code, locale, title: shownTitle, body: shownBody },
              {
                onSuccess: () => {
                  setConfirming(false);
                  setTitle(null);
                  setBody(null);
                  toast({ tone: 'success', message: `${code} saved` });
                },
                onError: (error) => {
                  setConfirming(false);
                  toast({ tone: 'error', message: errorMessage(error) });
                },
              },
            )
          }
        />
      )}
    </>
  );
}
